import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Button } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { IOfferDetails, IUser } from '../Interfaces';

interface IUserSelection {
  candidates: IOfferDetails[];
  selectedCandidateId: string | null;
  setSelectedCandidateId: (id: string) => void;
  totalCandidates: number;
  getStatusColor: (candidate: IOfferDetails) => string;
  getUserInitials: (user: IUser) => string;
  selectedCandidate: IOfferDetails | null;
  jobOpeningId: string;
  setCandidates?: (candidates: IOfferDetails[]) => void;
  addCandidatesFromPreviousRound?: (candidateIds: string[]) => void;
  limit: number;
  page: number;
  onNextPageChange?: (page: number) => void;
  onPreviousPageChange?: (page: number) => void;
}

export const UserSelection: React.FC<IUserSelection> = (props) => {
  const { t } = useTranslation(NS.adminPages);
  const US = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.userSelection;
  const {
    selectedCandidate,
    candidates = [],
    selectedCandidateId,
    setSelectedCandidateId,
    totalCandidates,
    getUserInitials,
    limit,
    page,
    onNextPageChange,
    onPreviousPageChange,
  } = props;

  const getGradient = (status?: string) => {
    switch (status) {
      case 'Offer-Extended':
        return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
      case 'Offer-Accepted':
        return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'Offer-Declined':
        return 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
      case 'Offer-Withheld':
        return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      default:
        return 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)';
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        borderRadius: 4,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        mb: 2,
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: 3,
            p: 2,
            width: '100%',
          }}
        >
          {candidates.map((candidate) => {
            const isActive = selectedCandidateId === candidate.jobApplicantId;
            const aiScore = candidate.interviewApplicant?.aiOverallScore?.score || 0;
            const initials = getUserInitials(candidate.interviewApplicant?.user);
            const user = candidate.interviewApplicant?.user || {
              firstName: '',
              lastName: '',
            };

            return (
              <Box
                key={candidate.jobApplicantId}
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelectedCandidateId(candidate.jobApplicantId)}
                  title={`${user.firstName} ${user.lastName} | AI Score: ${aiScore}% | Status: ${candidate.applicant_status}`}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    border: 'none',
                    p: 0,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    outline: isActive ? '3px solid' : 'none',
                    outlineColor: 'primary.main',
                    boxShadow: isActive ? 3 : 0,
                    transform: isActive ? 'scale(1.1)' : 'none',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '15px',
                      userSelect: 'none',
                      background: getGradient(candidate.applicant_status),
                    }}
                  >
                    {initials}
                  </Box>
                </Box>

                {/* AI Score below avatar */}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    mt: 0.5,
                    color: 'text.secondary',
                    cursor: 'help',
                    fontSize: '11px',
                  }}
                  title="AI Overall Score"
                >
                  {aiScore}%
                </Typography>

                {/* Status text */}
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    maxWidth: 70,
                    color:
                      candidate.applicant_status === 'Offer-Accepted'
                        ? 'success.main'
                        : candidate.applicant_status === 'Offer-Extended'
                          ? 'primary.main'
                          : candidate.applicant_status === 'Offer-Declined'
                            ? 'warning.dark'
                            : candidate.applicant_status === 'Offer-Withheld'
                              ? 'warning.main'
                              : 'text.secondary',
                  }}
                >
                  {candidate.applicant_status && candidate.applicant_status.includes('Offer-')
                    ? candidate.applicant_status.replace('Offer-', '')
                    : candidate.applicant_status || 'Unknown'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Summary stats with status legend */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: 'action.hover',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t(US.total)}: {totalCandidates}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t(US.selected)}: {selectedCandidate ? 1 : 0}/{candidates.length}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t(US.avgScore)}:{' '}
            {Math.round(
              candidates.length > 0
                ? candidates.reduce(
                    (sum, c) => sum + (c.interviewApplicant?.aiOverallScore?.score || 0),
                    0,
                  ) / candidates.length
                : 0,
            )}
            %
          </Typography>
        </Box>

        {totalCandidates > limit && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onPreviousPageChange ? () => onPreviousPageChange(Math.max(1, page - 1)) : undefined}
              disabled={page === 1}
              sx={{ textTransform: 'none', py: 0.25, px: 1, fontSize: '12px' }}
            >
              {t(US.previous)}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t(US.page)} {page} {t(US.of)} {Math.ceil(totalCandidates / limit)}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={onNextPageChange ? () => onNextPageChange(page + 1) : undefined}
              disabled={page * limit >= totalCandidates}
              sx={{ textTransform: 'none', py: 0.25, px: 1, fontSize: '12px' }}
            >
              {t(US.next)}
            </Button>
          </Box>
        )}

        {/* Status legends */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[
            { key: 'Extended', label: t(US.status.extended), color: '#3b82f6' },
            { key: 'Accepted', label: t(US.status.accepted), color: '#10b981' },
            { key: 'Declined', label: t(US.status.declined), color: '#f97316' },
            { key: 'Withheld', label: t(US.status.withheld), color: '#f59e0b' },
          ].map(({ key: status, label: statusLabel, color }) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
              <Typography variant="caption" color="text.secondary">
                {statusLabel}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default UserSelection;
